/**
 * Desi Calculator Component
 * Handles desi weight calculation for shipping packages
 */

class DesiCalculator {
  constructor() {
    this.form = document.getElementById('desi-calculator-form');
    this.inputs = {
      length: document.getElementById('desi-length'),
      width: document.getElementById('desi-width'),
      height: document.getElementById('desi-height'),
      weight: document.getElementById('desi-weight')
    };
    this.calculateBtn = document.getElementById('calculate-desi');
    this.resetBtn = document.getElementById('reset-form');
    this.resultSection = document.getElementById('desi-result');
    this.copyBtn = document.getElementById('copy-result');
    
    this.resultElements = {
      volumetricWeight: document.getElementById('volumetric-weight'),
      actualWeight: document.getElementById('actual-weight'),
      finalDesi: document.getElementById('final-desi')
    };

    this.init();
  }

  init() {
    if (!this.form) return;

    this.bindEvents();
    this.setupRealTimeCalculation();
  }

  bindEvents() {
    // Calculate button
    this.calculateBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.calculateDesi();
    });

    // Reset button
    this.resetBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.resetForm();
    });

    // Copy result button
    this.copyBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.copyResult();
    });

    // Form validation on input
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.addEventListener('input', () => {
          this.validateInput(input);
        });

        input.addEventListener('blur', () => {
          this.validateInput(input);
        });
      }
    });

    // Enter key submission
    this.form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.calculateDesi();
      }
    });
  }

  setupRealTimeCalculation() {
    // Optional: Calculate as user types (with debouncing)
    let timeout;
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.addEventListener('input', () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            if (this.isFormValid(false)) {
              this.calculateDesi(true); // Silent calculation
            }
          }, 500);
        });
      }
    });
  }

  validateInput(input) {
    const value = parseFloat(input.value);
    const field = input.closest('.form-field');
    
    // Remove previous error states
    input.classList.remove('error');
    field?.classList.remove('error');

    // Validate
    if (input.hasAttribute('required') && (!input.value || value <= 0)) {
      input.classList.add('error');
      field?.classList.add('error');
      return false;
    }

    if (input.value && (isNaN(value) || value < 0)) {
      input.classList.add('error');
      field?.classList.add('error');
      return false;
    }

    return true;
  }

  isFormValid(showErrors = true) {
    let isValid = true;

    Object.values(this.inputs).forEach(input => {
      if (input) {
        const inputValid = this.validateInput(input);
        if (!inputValid) {
          isValid = false;
        }
      }
    });

    return isValid;
  }

  calculateDesi(silent = false) {
    if (!this.isFormValid()) {
      if (!silent) {
        this.showError('Lütfen tüm alanları doğru şekilde doldurun.');
      }
      return;
    }

    // Show loading state
    if (!silent) {
      this.calculateBtn.classList.add('loading');
    }

    // Get values
    const length = parseFloat(this.inputs.length.value) || 0;
    const width = parseFloat(this.inputs.width.value) || 0;
    const height = parseFloat(this.inputs.height.value) || 0;
    const actualWeight = parseFloat(this.inputs.weight.value) || 0;

    // Calculate volumetric weight (L × W × H / 3000)
    const volumetricWeight = (length * width * height) / 3000;

    // Desi weight is the higher of volumetric weight or actual weight
    const desiWeight = Math.max(volumetricWeight, actualWeight);

    // Update results
    setTimeout(() => {
      this.displayResults({
        volumetricWeight: volumetricWeight,
        actualWeight: actualWeight,
        desiWeight: desiWeight
      });

      if (!silent) {
        this.calculateBtn.classList.remove('loading');
        this.showResult();
      }
    }, silent ? 0 : 300);
  }

  displayResults(results) {
    if (this.resultElements.volumetricWeight) {
      this.resultElements.volumetricWeight.textContent = 
        this.formatWeight(results.volumetricWeight);
    }

    if (this.resultElements.actualWeight) {
      this.resultElements.actualWeight.textContent = 
        this.formatWeight(results.actualWeight);
    }

    if (this.resultElements.finalDesi) {
      this.resultElements.finalDesi.textContent = 
        this.formatWeight(results.desiWeight);
    }
  }

  formatWeight(weight) {
    return `${weight.toFixed(2)} kg`;
  }

  showResult() {
    if (this.resultSection) {
      this.resultSection.style.display = 'block';
      this.resultSection.classList.add('show');
      
      // Smooth scroll to result
      setTimeout(() => {
        this.resultSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 100);
    }
  }

  hideResult() {
    if (this.resultSection) {
      this.resultSection.style.display = 'none';
      this.resultSection.classList.remove('show');
    }
  }

  resetForm() {
    // Clear all inputs
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.value = '';
        input.classList.remove('error');
        const field = input.closest('.form-field');
        field?.classList.remove('error');
      }
    });

    // Hide result
    this.hideResult();

    // Focus first input
    if (this.inputs.length) {
      this.inputs.length.focus();
    }
  }

  async copyResult() {
    if (!this.resultElements.finalDesi) return;

    const desiValue = this.resultElements.finalDesi.textContent;
    const volumetricValue = this.resultElements.volumetricWeight.textContent;
    const actualValue = this.resultElements.actualWeight.textContent;

    const textToCopy = `Desi Hesaplama Sonucu:
Hacimsel Ağırlık: ${volumetricValue}
Gerçek Ağırlık: ${actualValue}
Desi Ağırlığı: ${desiValue}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      this.showCopyFeedback();
    } catch (err) {
      // Fallback for older browsers
      this.fallbackCopyToClipboard(textToCopy);
    }
  }

  fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showCopyFeedback();
    } catch (err) {
      console.error('Copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  showCopyFeedback() {
    if (!this.copyBtn) return;

    const originalText = this.copyBtn.textContent;
    
    this.copyBtn.classList.add('copied');
    this.copyBtn.textContent = 'Kopyalandı!';

    setTimeout(() => {
      this.copyBtn.classList.remove('copied');
      this.copyBtn.textContent = originalText;
    }, 2000);
  }

  showError(message) {
    // You can implement a toast/alert system here
    // For now, using browser alert
    alert(message);
  }
}

// Utility function to format numbers
function formatNumber(num, decimals = 2) {
  return parseFloat(num).toFixed(decimals);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new DesiCalculator();
});

// Re-initialize for theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('desi-calculator')) {
    new DesiCalculator();
  }
});

// Handle theme editor unload
document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId.includes('desi-calculator')) {
    // Cleanup if needed
  }
});